import Axios from 'axios';
import { Config } from '../apiRoutes';
import { authHeaders, apiErrorMessage } from '../headers';

import {
  ORDER_SHIPMENTS_INDEX_REQUESTED,
  ORDER_SHIPMENTS_INDEX_SUCCESS,
  ORDER_SHIPMENTS_INDEX_FAILURE,
  ORDER_SHIPMENT_PREPARE_REQUESTED,
  ORDER_SHIPMENT_PREPARE_SUCCESS,
  ORDER_SHIPMENT_PREPARE_FAILURE,
  ORDER_SHIPMENTS_PREPARE_RESET,
  ORDER_SHIPMENTS_PREPARE_ALL_REQUESTED,
  ORDER_SHIPMENTS_PREPARE_ALL_SUCCESS,
  ORDER_SHIPMENTS_PREPARE_ALL_FAILURE,
  ORDER_SHIPMENTS_CONVERT_TO_TOTES_REQUESTED,
  ORDER_SHIPMENTS_CONVERT_TO_TOTES_SUCCESS,
  ORDER_SHIPMENTS_CONVERT_TO_TOTES_FAILURE,
} from '../types';

const PREPARED_SHIPMENTS_LIMIT = 100;
const PREPARE_PAGE_LIMIT = 50;

const prepareOrderShipmentsPage = async (cursor) => {
  const endPoint = Config.baseUrl + Config.orderShipmentsPrepare;

  const response = await Axios.post(endPoint, {}, {
    headers: await authHeaders(),
    params: {
      cursor,
      limit: PREPARE_PAGE_LIMIT
    }
  });

  return response.data;
};

export const orderShipmentsIndexRequested = (append = false) => {
  return {
    type: ORDER_SHIPMENTS_INDEX_REQUESTED,
    append: append
  }
};

export const orderShipmentsIndexSuccess = (orderShipments, append = false) => {
  return {
    type: ORDER_SHIPMENTS_INDEX_SUCCESS,
    payload: orderShipments,
    append: append
  }
};

export const orderShipmentsIndexFailure = (error) => {
  return {
    type: ORDER_SHIPMENTS_INDEX_FAILURE,
    error: error
  }
};

export const orderShipmentPrepareRequested = () => {
  return {
    type: ORDER_SHIPMENT_PREPARE_REQUESTED
  }
};

export const orderShipmentPrepareSuccess = (result) => {
  return {
    type: ORDER_SHIPMENT_PREPARE_SUCCESS,
    payload: result
  }
};

export const orderShipmentPrepareFailure = (error) => {
  return {
    type: ORDER_SHIPMENT_PREPARE_FAILURE,
    error: error
  }
};

export const orderShipmentsPrepareReset = () => {
  return {
    type: ORDER_SHIPMENTS_PREPARE_RESET
  }
};

export const orderShipmentsPrepareAllRequested = () => {
  return {
    type: ORDER_SHIPMENTS_PREPARE_ALL_REQUESTED
  }
};

export const orderShipmentsPrepareAllSuccess = (result) => {
  return {
    type: ORDER_SHIPMENTS_PREPARE_ALL_SUCCESS,
    payload: result
  }
};

export const orderShipmentsPrepareAllFailure = (error) => {
  return {
    type: ORDER_SHIPMENTS_PREPARE_ALL_FAILURE,
    error: error
  }
};

export const orderShipmentsIndex = (continuationToken = null, append = false) => {
  return async (dispatch) => {
    dispatch(orderShipmentsIndexRequested(append));

    const endPoint = Config.baseUrl + Config.orderShipments;
    const params = { limit: PREPARED_SHIPMENTS_LIMIT };

    if (continuationToken) {
      params.continuation_token = continuationToken;
    }

    return Axios.get(endPoint, { headers: await authHeaders(), params: params })
      .then((response) => {
        dispatch(orderShipmentsIndexSuccess(response.data, append));

        if (response.data.continuation_token) {
          return dispatch(orderShipmentsIndex(response.data.continuation_token, true));
        }

        return response.data;
      })
      .catch((error) => {
        dispatch(orderShipmentsIndexFailure(apiErrorMessage(error, 'Could not load prepared order shipments.')));
      });
  }
};

export const orderShipmentPrepare = (cursor = null) => {
  return async (dispatch) => {
    dispatch(orderShipmentPrepareRequested());

    try {
      const result = await prepareOrderShipmentsPage(cursor);
      dispatch(orderShipmentPrepareSuccess(result));
      await dispatch(orderShipmentsIndex());
      return result;
    } catch(error) {
      dispatch(orderShipmentPrepareFailure(apiErrorMessage(error, 'Could not prepare the next order shipment page.')));
      return null;
    }
  }
};

export const orderShipmentsConvertToTotesRequested = () => {
  return {
    type: ORDER_SHIPMENTS_CONVERT_TO_TOTES_REQUESTED
  }
};

export const orderShipmentsConvertToTotesSuccess = (result) => {
  return {
    type: ORDER_SHIPMENTS_CONVERT_TO_TOTES_SUCCESS,
    payload: result
  }
};

export const orderShipmentsConvertToTotesFailure = (error) => {
  return {
    type: ORDER_SHIPMENTS_CONVERT_TO_TOTES_FAILURE,
    error: error
  }
};

export const orderShipmentsConvertToTotes = (orderShipmentIds, cartLayoutId) => {
  return async (dispatch) => {
    dispatch(orderShipmentsConvertToTotesRequested());

    const endPoint = Config.baseUrl + Config.orderShipmentsConvertToTotes;

    try {
      const response = await Axios.post(endPoint, { order_shipment_ids: orderShipmentIds, cart_layout_id: cartLayoutId }, {
        headers: await authHeaders()
      });

      dispatch(orderShipmentsConvertToTotesSuccess(response.data));
      return response.data;
    } catch(error) {
      dispatch(orderShipmentsConvertToTotesFailure(apiErrorMessage(error, 'Could not convert order shipments to totes.')));
      return null;
    }
  }
};

export const orderShipmentsPrepareAll = () => {
  return async (dispatch) => {
    dispatch(orderShipmentsPrepareAllRequested());

    let nextCursor = null;
    let result = null;
    const totals = {
      pages_prepared: 0,
      prepared_shipment_count: 0,
      prepared_order_count: 0,
      skipped_prepared_order_count: 0,
      marked_order_count: 0,
      order_count: 0
    };

    try {
      do {
        result = await prepareOrderShipmentsPage(nextCursor);
        dispatch(orderShipmentPrepareSuccess(result));
        await dispatch(orderShipmentsIndex());

        totals.pages_prepared += 1;
        totals.prepared_shipment_count += result.prepared_shipment_count || 0;
        totals.prepared_order_count += result.prepared_order_count || 0;
        totals.skipped_prepared_order_count += result.skipped_prepared_order_count || 0;
        totals.marked_order_count += result.marked_order_count || 0;
        totals.order_count += result.order_count || 0;

        nextCursor = result.next_cursor;
      } while (nextCursor);

      dispatch(orderShipmentsPrepareAllSuccess(totals));
      return totals;
    } catch(error) {
      dispatch(orderShipmentsPrepareAllFailure(apiErrorMessage(error, 'Could not prepare all order shipments.')));
      return null;
    }
  }
};
